import "./QuestionCard.scss";

import { Button, Flex, Segment, Text } from "@fluentui/react-northstar";
import { FieldDescriptors } from "@shopify/react-form-state";
import {
  NominalQuestion,
  OpenEndedQuestion,
  OrdinalQuestion,
  OrdinalQuestionChoice,
  Question,
  QuestionContentType,
  QuestionTemplate,
  QuestionType,
} from "@teams/types";
import React, { useMemo } from "react";

import { PollyIcon, PollyIconName } from "../../../../../_shared/components/PollyIcon";
import { AuthoringFields } from "../types";
import { QuestionCardMenu } from "./QuestionCardMenu";

export const CollapsedQuestion: React.FC<{
  compressed?: boolean;
  isLimitedEdit?: boolean;
  fields: FieldDescriptors<AuthoringFields>;
  questionField: FieldDescriptors<Question<QuestionType>>;
  questionValue: Question<QuestionType>;
  questionTemplates: QuestionTemplate[];
  questionIndex: number;
  addQuestion: (
    fields: FieldDescriptors<AuthoringFields>,
    question: Question<QuestionType>
  ) => void;
  setIsExpanded: (isExpanded: boolean) => void;
}> = ({
  compressed,
  isLimitedEdit,
  fields,
  questionField,
  questionValue,
  questionTemplates,
  questionIndex,
  addQuestion,
  setIsExpanded,
}) => {
  const [questionTypeTitle, questionTypeIcon] = useMemo(() => {
    switch (questionField.questionType.value) {
      case "nominal":
        return [fields.type.value === "quiz" ? "Quiz" : "Multiple choice", "multiple-choice-icon"];
      case "ordinal":
        return [
          questionTemplates.find(x => x.id === questionValue.templateId)?.label,
          "rating-icon-borderless",
        ];
      case "open-ended":
        return [
          getOpenEndedQuestionTitle((questionValue as OpenEndedQuestion).contentType),
          "open-text-icon",
        ];
    }
  }, [fields.type.value, questionField.questionType.value, questionValue, questionTemplates]);

  const enabledCheckboxFields: string[] = useMemo(() => {
    const enabledFields = [];

    if (questionField.required.value) {
      enabledFields.push("Required");
    }

    if (questionField.questionType.value === "nominal") {
      if ((questionField as FieldDescriptors<NominalQuestion>).allowMultipleChoices.value) {
        if (fields.type.value === "quiz") {
          enabledFields.push("Multiple correct");
        } else {
          enabledFields.push("Allow multiple votes");
        }
      }
      if (
        (questionField as FieldDescriptors<NominalQuestion>).allowUserAddOptions.value &&
        fields.type.value === "poll"
      ) {
        enabledFields.push("Audience can add choices");
      }
    }

    if (questionField.allowComments?.value) {
      enabledFields.push("Allow comments");
    }

    return enabledFields;
  }, [fields.type.value, questionField]);

  return (
    <Segment>
      <Flex column gap="gap.small">
        <Flex gap="gap.small" vAlign="start">
          <Flex.Item grow>
            <Flex vAlign="center" gap="gap.small">
              <PollyIcon icon={questionTypeIcon as PollyIconName} inheritColor />
              <Text className="question-type-collapsed" content={questionTypeTitle} />
            </Flex>
          </Flex.Item>
          <Flex.Item push>
            <QuestionCardMenu
              compressed={compressed}
              isLimitedEdit={isLimitedEdit}
              fields={fields}
              question={questionField}
              questionTemplates={questionTemplates}
              addQuestion={addQuestion}
            />
          </Flex.Item>
        </Flex>
        <Text
          className="question-title"
          content={`${fields.questions.value.length > 1 ? `Q${questionIndex + 1}: ` : ""}${
            questionField.label.value
          }`}
        />
        {["nominal", "ordinal"].includes(questionField.questionType.value) && (
          <Flex gap="gap.small" wrap>
            {(questionValue as NominalQuestion | OrdinalQuestion).choices.map(choice => {
              return (
                <div className="nominal-choice-collapsed-container" key={choice.id}>
                  <Text
                    content={(choice as OrdinalQuestionChoice).label ?? choice.value}
                    className="nominal-choice-collapsed"
                  />
                </div>
              );
            })}
          </Flex>
        )}
        <Flex vAlign="center" wrap>
          <Flex.Item grow>
            <>
              {enabledCheckboxFields.map((field, index) => {
                return (
                  <>
                    {index > 0 && (
                      <>
                        <span>&nbsp;&nbsp;</span> | <span>&nbsp;&nbsp;</span>
                      </>
                    )}
                    <Text className="checkbox-fields-collapsed" content={field} />{" "}
                  </>
                );
              })}
            </>
          </Flex.Item>
          <Flex.Item push>
            <Button
              icon={<PollyIcon icon="edit-icon" inheritColor />}
              content="Edit"
              size="medium"
              onClick={() => {
                setIsExpanded(true);
              }}
            />
          </Flex.Item>
        </Flex>
      </Flex>
    </Segment>
  );
};

function getOpenEndedQuestionTitle(contentType: QuestionContentType) {
  switch (contentType) {
    case "text/date":
      return "Open Date";
    case "text/time":
      return "Open Time";
    case "text/plain":
      return "Open Text";
  }
}
